import { createMultiStyleConfigHelpers, extendTheme } from '@chakra-ui/react';
import colors from './foundations/colors';
import shadows from './foundations/shadows';
import radii from './foundations/radii';
import breakpoints from './foundations/breakpoints';
import {
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
} from './foundations/typography';
import semanticTokens from './semanticTokens';
import Button from './components/Button';
import Input from './components/Input';
import Select from './components/Select';
import Textarea from './components/Textarea';
import Link from './components/Link';
import Card from './components/Card';
import Heading from './components/Heading';
import Checkbox from './components/Checkbox';
import Switch from './components/Switch';
import FormLabel from './components/FormLabel';

const publicOverlaySurface = {
  bg: 'white',
  color: 'text.primary',
  borderRadius: 'xl',
  boxShadow: '2xl',
};

const dialogParts = ['overlay', 'dialogContainer', 'dialog', 'header', 'closeButton', 'body', 'footer'];
const modalStyles = createMultiStyleConfigHelpers(dialogParts);
const drawerStyles = createMultiStyleConfigHelpers(dialogParts);
const menuStyles = createMultiStyleConfigHelpers(['button', 'list', 'item', 'groupTitle', 'command', 'divider']);
const popoverStyles = createMultiStyleConfigHelpers(['popper', 'content', 'header', 'body', 'footer', 'arrow', 'closeButton']);

const Modal = modalStyles.defineMultiStyleConfig({
  variants: {
    publicSurface: modalStyles.definePartsStyle({
      overlay: { bg: 'blackAlpha.600', backdropFilter: 'blur(2px)' },
      dialog: {
        ...publicOverlaySurface,
        mx: { base: '16px', sm: 'auto' },
        my: { base: '16px', sm: 'auto' },
        maxW: { base: 'calc(100vw - 32px)', sm: '600px' },
      },
      header: { bg: 'white', color: 'brand.900', borderColor: 'border.default' },
      body: { bg: 'white', color: 'text.primary' },
      footer: { bg: 'white', borderColor: 'border.default' },
    }),
  },
  defaultProps: { variant: 'publicSurface' },
});

const Drawer = drawerStyles.defineMultiStyleConfig({
  variants: {
    publicSurface: drawerStyles.definePartsStyle({
      overlay: { bg: 'blackAlpha.600', backdropFilter: 'blur(2px)' },
      dialog: {
        bg: 'white',
        color: 'text.primary',
        boxShadow: '2xl',
        h: '100%',
        m: '0 !important',
        borderRadius: '0 !important',
      },
      header: { bg: 'white', color: 'brand.900', borderColor: 'border.default' },
      body: { bg: 'transparent' },
      footer: { bg: 'white', borderColor: 'border.default' },
    }),
  },
  defaultProps: { variant: 'publicSurface' },
});

const Menu = menuStyles.defineMultiStyleConfig({
  variants: {
    publicSurface: menuStyles.definePartsStyle({
      list: {
        bg: 'white', color: 'text.primary', borderColor: 'border.default',
        boxShadow: 'lg', borderRadius: 'lg', py: 2,
      },
      item: {
        bg: 'white', color: 'text.primary', minH: '44px',
        _hover: { bg: 'surface.blue', color: 'brand.700' },
        _focus: { bg: 'surface.blue', color: 'brand.700' },
      },
    }),
  },
  defaultProps: { variant: 'publicSurface' },
});

const Popover = popoverStyles.defineMultiStyleConfig({
  variants: {
    publicSurface: popoverStyles.definePartsStyle({
      content: {
        ...publicOverlaySurface,
        border: '1px solid',
        borderColor: 'border.default',
      },
    }),
  },
  defaultProps: { variant: 'publicSurface' },
});

const theme = extendTheme({
  config: { initialColorMode: 'light', useSystemColorMode: false },
  colors,
  shadows,
  radii,
  breakpoints,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  semanticTokens,
  styles: {
    global: {
      'html, body': {
        color: 'text.primary',
        bg: 'surface.default',
        fontSize: fontSizes.md,
        lineHeight: lineHeights.base,
      },
      body: { overflowX: 'hidden' },
      '*::selection': { bg: 'brand.100' },
      '@media (prefers-reduced-motion: reduce)': {
        '*, *::before, *::after': {
          animationDuration: '0.01ms !important',
          animationIterationCount: '1 !important',
          transitionDuration: '0.01ms !important',
          scrollBehavior: 'auto !important',
        },
      },
    },
  },
  components: {
    Button, Input, Select, Textarea, Checkbox, Switch, FormLabel, Link, Card, Heading, Modal, Drawer, Menu, Popover,
  },
});

export default theme;
